const W = 1080;
const MIN_H = 1350;

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const wrap = (ctx, text, maxWidth) => {
  const lines = [];
  let line = "";
  for (const word of text.split(/\s+/)) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else line = test;
  }
  if (line) lines.push(line);
  return lines;
};

const fillJustified = (ctx, line, x, y, width, isLast) => {
  const wordsInLine = line.split(" ");
  if (isLast || wordsInLine.length === 1) {
    ctx.textAlign = "left";
    ctx.fillText(line, x, y);
    return;
  }
  const wordsWidth = wordsInLine.reduce((s, w) => s + ctx.measureText(w).width, 0);
  const gap = (width - wordsWidth) / (wordsInLine.length - 1);
  ctx.textAlign = "left";
  let cx = x;
  for (const w of wordsInLine) {
    ctx.fillText(w, cx, y);
    cx += ctx.measureText(w).width + gap;
  }
};

export const buildShareCard = async ({ brand, question, verdict, answer, image, footer }) => {
  await Promise.all([
    document.fonts.load('300 44px "Cormorant Garamond"'),
    document.fonts.load('italic 300 44px "Cormorant Garamond"'),
    document.fonts.load('italic 600 44px "Cormorant Garamond"'),
    document.fonts.load('500 22px "Outfit"'),
  ]);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const textWidth = 900;
  const qFont = 'italic 600 40px "Cormorant Garamond"';
  const aFont = '300 46px "Cormorant Garamond"';
  const cx = W / 2;
  const cy = 270;
  const r = 120;
  ctx.font = qFont;
  const qLines = question ? wrap(ctx, `« ${question} »`, textWidth) : [];
  ctx.font = aFont;
  const answerLines = wrap(ctx, answer, textWidth);
  const contentHeight =
    cy + r + 100 + (qLines.length ? qLines.length * 50 + 34 : 0) + (verdict ? 150 : 0) + answerLines.length * 60 + 140;
  const H = Math.max(MIN_H, contentHeight);
  canvas.width = W;
  canvas.height = H;

  ctx.fillStyle = "#0D0726";
  ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W / 2, 290, 30, W / 2, 290, 520);
  glow.addColorStop(0, "rgba(139,92,246,0.42)");
  glow.addColorStop(0.5, "rgba(245,158,11,0.10)");
  glow.addColorStop(1, "rgba(13,7,38,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  let seed = 7;
  const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
  for (let i = 0; i < Math.round(260 * (H / MIN_H)); i++) {
    const rr = rnd() * 1.6 + 0.3;
    ctx.fillStyle = `rgba(255,255,255,${0.25 + rnd() * 0.6})`;
    ctx.beginPath();
    ctx.arc(rnd() * W, rnd() * H, rr, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.textAlign = "center";
  ctx.fillStyle = "rgba(245,158,11,0.9)";
  ctx.font = '500 30px "Outfit"';
  ctx.letterSpacing = "12px";
  ctx.fillText(brand.toUpperCase(), W / 2, 100);
  ctx.letterSpacing = "0px";

  ctx.save();
  ctx.shadowColor = "rgba(139,92,246,0.75)";
  ctx.shadowBlur = 90;
  ctx.fillStyle = "#1a0f3d";
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  if (image) {
    try {
      const img = await loadImage(image);
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.clip();
      const s = Math.max((r * 2) / img.width, (r * 2) / img.height);
      ctx.drawImage(img, cx - (img.width * s) / 2, cy - (img.height * s) / 2, img.width * s, img.height * s);
      const shade = ctx.createRadialGradient(cx - r * 0.4, cy - r * 0.5, r * 0.1, cx, cy, r);
      shade.addColorStop(0, "rgba(255,255,255,0.22)");
      shade.addColorStop(0.5, "rgba(255,255,255,0)");
      shade.addColorStop(1, "rgba(3,0,20,0.55)");
      ctx.fillStyle = shade;
      ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
      ctx.restore();
    } catch {
      /* la vision reste voilée */
    }
  }
  ctx.strokeStyle = "rgba(255,255,255,0.18)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();

  const ped = ctx.createLinearGradient(cx - 150, 0, cx + 150, 0);
  ped.addColorStop(0, "rgba(245,158,11,0)");
  ped.addColorStop(0.5, "rgba(245,158,11,0.6)");
  ped.addColorStop(1, "rgba(245,158,11,0)");
  ctx.fillStyle = ped;
  ctx.fillRect(cx - 150, cy + r + 22, 300, 3);

  let y = cy + r + 100;
  ctx.textAlign = "center";
  if (qLines.length) {
    ctx.fillStyle = "#D4A017";
    ctx.shadowColor = "rgba(212,160,23,0.35)";
    ctx.shadowBlur = 18;
    ctx.font = qFont;
    for (const line of qLines) {
      ctx.fillText(line, cx, y);
      y += 50;
    }
    ctx.shadowBlur = 0;
    y += 34;
  }

  if (verdict) {
    ctx.fillStyle = "#FBBF24";
    ctx.font = '300 140px "Cormorant Garamond"';
    ctx.shadowColor = "rgba(245,158,11,0.5)";
    ctx.shadowBlur = 40;
    ctx.fillText(verdict, cx, y + 60);
    ctx.shadowBlur = 0;
    y += 150;
  }

  ctx.fillStyle = "rgba(255,255,255,0.97)";
  ctx.font = aFont;
  answerLines.forEach((line, i) => {
    fillJustified(ctx, line, cx - textWidth / 2, y, textWidth, i === answerLines.length - 1);
    y += 60;
  });

  ctx.textAlign = "center";
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.font = 'italic 300 30px "Cormorant Garamond"';
  ctx.fillText(footer, cx, H - 64);

  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
};

export const shareOrDownload = async (blob, filename, shareTitle) => {
  const file = new File([blob], filename, { type: "image/png" });
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: shareTitle });
      return "shared";
    } catch {
      /* repli sur le téléchargement */
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return "downloaded";
};
